import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';

const AUTOPLAY_MS = 5500;

export const AUTH_CAROUSEL_SLIDES = [
  {
    src: '/auth/slide-workspace.svg',
    titleKey: 'authCarouselSlide1Title',
    captionKey: 'authCarouselSlide1Caption'
  },
  {
    src: '/auth/slide-preview.svg',
    titleKey: 'authCarouselSlide2Title',
    captionKey: 'authCarouselSlide2Caption'
  },
  {
    src: '/auth/slide-projects.svg',
    titleKey: 'authCarouselSlide3Title',
    captionKey: 'authCarouselSlide3Caption'
  },
  {
    src: '/auth/slide-bilingual.svg',
    titleKey: 'authCarouselSlide4Title',
    captionKey: 'authCarouselSlide4Caption'
  }
];

export const AuthCarousel = ({ className = '' }) => {
  const { t, language } = useI18n();
  const [activeIndex, setActiveIndex] = useState(0);
  const slideCount = AUTH_CAROUSEL_SLIDES.length;
  const PrevIcon = language === 'fa' ? ChevronRight : ChevronLeft;
  const NextIcon = language === 'fa' ? ChevronLeft : ChevronRight;

  const goTo = useCallback((index) => {
    setActiveIndex((index + slideCount) % slideCount);
  }, [slideCount]);

  const goNext = useCallback(() => {
    goTo(activeIndex + 1);
  }, [activeIndex, goTo]);

  const goPrev = useCallback(() => {
    goTo(activeIndex - 1);
  }, [activeIndex, goTo]);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      return undefined;
    }

    const intervalId = window.setInterval(goNext, AUTOPLAY_MS);
    return () => window.clearInterval(intervalId);
  }, [goNext]);

  const activeSlide = AUTH_CAROUSEL_SLIDES[activeIndex];

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className={`relative ${className}`}
      aria-roledescription="carousel"
      aria-label={t('authCarouselLabel')}
    >
      <motion.div
        layout
        className="glass-panel relative overflow-hidden rounded-[2rem] border border-border bg-panel shadow-floating"
      >
        <div className="relative aspect-[4/3] w-full overflow-hidden bg-panel-strong sm:aspect-[16/11]">
          <AnimatePresence mode="wait" initial={false}>
            <motion.img
              key={activeSlide.src}
              src={activeSlide.src}
              alt={t(activeSlide.titleKey)}
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 h-full w-full object-cover"
              draggable={false}
            />
          </AnimatePresence>

          <motion.div
            key={`${activeSlide.src}-caption`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.35 }}
            className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/75 via-slate-950/35 to-transparent px-5 pb-5 pt-16 text-white sm:px-6 sm:pb-6"
          >
            <p className="text-sm font-bold sm:text-base">{t(activeSlide.titleKey)}</p>
            <p className="mt-1 text-xs leading-6 text-white/80 sm:text-sm">{t(activeSlide.captionKey)}</p>
          </motion.div>
        </div>

        <div
          layout
          className="flex items-center justify-between gap-3 border-t border-border/80 px-4 py-3 sm:px-5"
        >
          <div className="flex items-center gap-2">
            {AUTH_CAROUSEL_SLIDES.map((slide, index) => (
              <button
                key={slide.src}
                type="button"
                onClick={() => goTo(index)}
                className={`h-2.5 rounded-full transition-all ${
                  index === activeIndex ? 'w-7 bg-accent' : 'w-2.5 bg-border hover:bg-muted/60'
                }`}
                aria-label={t('authCarouselGoTo', { index: index + 1 })}
                aria-current={index === activeIndex ? 'true' : undefined}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goPrev}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-panel-strong text-text transition hover:bg-accent-soft"
              aria-label={t('authCarouselPrev')}
            >
              <PrevIcon size={18} />
            </button>
            <button
              type="button"
              onClick={goNext}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-panel-strong text-text transition hover:bg-accent-soft"
              aria-label={t('authCarouselNext')}
            >
              <NextIcon size={18} />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
